import { Operation } from "@/hooks/useListActiveOperations"
import { PhotoTour } from "@/hooks/usePhotoTour"
import { PlacementDetailsByOperationIdOutput } from "@/hooks/usePlacementDetailsByOperationId"
import { ScheduleMap } from "@/model/schedule"
import { ListingOccupancy } from "@/model/listingOccupancy"
import { ListingOccupancyByOperationIdsOutput } from "@/hooks/useListingOccupancyByOperationIds"

export type JoinedOperation = Operation & {
    placementName: string
    hasCheckin: boolean
    hasErrands: boolean
    photoTour?: PhotoTour
    occupancy?: ListingOccupancy
}

const composeJoinedOperation = (
    op: Operation,
    additionalData: {
        photoTourByOperationIdMap: Record<string, PhotoTour>
        hasErrandsMap: Record<string, boolean>
        hasCheckinMap: Record<string, { hasCheckinOnDate: boolean } | undefined>
        placementDetailMap: PlacementDetailsByOperationIdOutput
        occupancyMap?: ListingOccupancyByOperationIdsOutput
    }
): { error: undefined; data: JoinedOperation } | { error: Error } => {
    const {
        placementDetailMap,
        photoTourByOperationIdMap,
        hasCheckinMap,
        hasErrandsMap,
        occupancyMap
    } = additionalData

    const placementDetail = placementDetailMap[op.operationId]
    // FIXME: ここのエラーは本当に必要？必要ないならコメント削除して良さそう
    // if (!placementDetail) return { error: new Error("placement not found") }

    const placementName =
        (placementDetail?.placement.placementType === "listing"
            ? placementDetail?.listing?.name
            : placementDetail?.commonArea?.name) ?? ""

    const photoTour = op.photoTourId
        ? photoTourByOperationIdMap[op.operationId]
        : undefined

    const hasCheckin = hasCheckinMap[op.operationId]?.hasCheckinOnDate ?? false
    const hasErrands = hasErrandsMap[op.operationId] ?? false
    const occupancy = occupancyMap?.[op.operationId]

    return {
        error: undefined,
        data: {
            ...op,
            placementName,
            photoTour,
            hasCheckin,
            hasErrands,
            occupancy
        }
    }
}

type JoinedScheduleMap = ScheduleMap<JoinedOperation>

const sortByScheduleStartAndListingName = (
    a: JoinedOperation,
    b: JoinedOperation
) => {
    // 第一優先: scheduleStart（早い、遅い、指定なし）
    const scheduleStartA = a.scheduleStart ?? Number.MAX_VALUE // 開始時刻がない場合は指定があるものに比べて緊急度が低い為、一番最後に表示されるように最大値を設定
    const scheduleStartB = b.scheduleStart ?? Number.MAX_VALUE
    if (scheduleStartA !== scheduleStartB) {
        return scheduleStartA - scheduleStartB
    }

    // 第二優先: deadlineAt（早い、遅い、指定なし）
    const deadlineA = a.deadlineAt ?? Number.MAX_VALUE
    const deadlineB = b.deadlineAt ?? Number.MAX_VALUE
    if (deadlineA !== deadlineB) {
        return deadlineA - deadlineB
    }

    // 第三優先: placement名
    return a.placementName.localeCompare(b.placementName, "ja")
}

export const composeJoinedScheduleMap = (
    arg: ScheduleMap<Operation>,
    additionalData: Parameters<typeof composeJoinedOperation>["1"]
): JoinedScheduleMap => {
    const result: JoinedScheduleMap = {}
    Object.entries(arg).forEach(([date, operations]) => {
        result[date] = operations
            .map(operation => {
                const composed = composeJoinedOperation(
                    operation,
                    additionalData
                )

                if (composed.error) {
                    console.error(composed.error)
                    return undefined
                }

                return composed.data
            })
            // undefined(データの不整合がある)時には除外するが、基本的に発生しないはず
            .filter((x): x is JoinedOperation => Boolean(x))
            .sort(sortByScheduleStartAndListingName)
    })
    return result
}
